import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Clock, Zap, ArrowDown, Trophy, Instagram, Recycle, Gift, Plus, Mail, Loader2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";
import PackCard from "@/components/PackCard";
import CartDrawer from "@/components/CartDrawer";
import Countdown from "@/components/Countdown";
import { TopoEmblem } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { SHOP } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FILTERS = [
  { id: "all", label: "Tout" },
  { id: "signature", label: "Signature" },
  { id: "cocktail", label: "Cocktails" },
  { id: "beer", label: "Bières" },
  { id: "soft", label: "Softs" },
  { id: "energy", label: "Energy" },
  { id: "juice", label: "Jus" },
  { id: "water", label: "Eau" },
  { id: "food", label: "Food" },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const { setOpen, setEcocupProduct, add } = useCart();

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API}/products`)
      .then((res) => {
        if (!mounted) return;
        setProducts(res.data);
        const ecocup = res.data.find((p) => p.id === "ecocup");
        if (ecocup) setEcocupProduct(ecocup);
      })
      .catch(() => {
        // Silent failure: product list will be empty and UI shows fallback message
      })
      .finally(() => setLoading(false));
    return () => {
      mounted = false;
    };
  }, [setEcocupProduct]);

  const drinks = useMemo(
    () => products.filter((p) => !p.is_deposit && !p.is_pack && p.category !== "food"),
    [products]
  );
  const foodProducts = useMemo(() => products.filter((p) => p.category === "food"), [products]);
  const drinksForPack = useMemo(
    () => products.filter((p) => !p.is_deposit && !p.is_pack && p.category !== "food"),
    [products]
  );
  const packs = useMemo(() => products.filter((p) => p.is_pack), [products]);
  const ecocup = useMemo(() => products.find((p) => p.is_deposit), [products]);

  // All non-deposit, non-pack items show up in the main grid (drinks + food)
  const catalogItems = useMemo(
    () => products.filter((p) => !p.is_deposit && !p.is_pack),
    [products]
  );

  const filtered = useMemo(() => {
    if (filter === "all") return catalogItems;
    return catalogItems.filter((p) => p.category === filter);
  }, [filter, catalogItems]);

  const totalStock = useMemo(
    () => drinks.reduce((s, p) => s + p.stock_remaining, 0) + foodProducts.reduce((s, p) => s + p.stock_remaining, 0),
    [drinks, foodProducts]
  );

  const [sales, setSales] = useState(null);
  useEffect(() => {
    axios.get(`${API}/config/sales`).then((r) => setSales(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#FDF0DF] text-[#14230A]">
      <Header />

      {/* HERO */}
      <section className="relative overflow-hidden border-b-2 border-[#14230A]">
        <div className="grain absolute inset-0 pointer-events-none" />
        {/* Topographic floating shapes */}
        <div className="absolute -right-20 top-10 opacity-[0.06] pointer-events-none rotate-12">
          <TopoEmblem className="w-[520px] h-[520px]" color="#14230A" />
        </div>
        <div className="absolute -left-32 bottom-0 opacity-[0.04] pointer-events-none -rotate-12">
          <TopoEmblem className="w-[420px] h-[420px]" color="#14230A" />
        </div>

        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-12 md:pt-20 pb-10 md:pb-16 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <div className="md:col-span-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="flex items-center gap-3 font-cond uppercase tracking-[0.3em] text-[11px] md:text-xs"
              >
                <span className="inline-block w-8 h-px bg-[#14230A]" />
                <span>Trésors de Guyane · Présente</span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="font-display text-[64px] sm:text-[88px] md:text-[140px] lg:text-[170px] leading-[0.85] mt-4 md:mt-6"
              >
                BOISSONS
                <br />
                <span className="text-[#FF4D00]">URBX</span> 2026
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-6 md:mt-10 max-w-xl text-base md:text-lg leading-relaxed text-[#14230A]/80"
              >
                Prévente exclusive de boissons pour l’URBX Festival 2026 à la Grand’Place de Roubaix. Stocks limités,
                paiement sécurisé, ticket QR envoyé direct sur ton email. Ecocup collector incluse
                (caution remboursée au stand) — sous l’une d’elles, un <b>iPhone 14 reconditionné</b> à
                remporter. Évite la queue au bar, viens, tu vibres.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <a
                  href="#catalog"
                  data-testid={SHOP.heroCtaShop}
                  className="inline-flex items-center gap-2 px-7 h-14 bg-[#14230A] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-sm font-bold btn-brut-orange hover:bg-[#FF4D00]"
                >
                  <Zap className="w-4 h-4" /> Réserver mes boissons
                </a>
                <a
                  href="#ecocup-concours"
                  className="inline-flex items-center gap-2 px-6 h-14 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#FF4D00] hover:border-[#FF4D00] hover:text-[#FDF0DF]"
                >
                  🏆 Jeux & cadeaux
                </a>
                <a
                  href="#event"
                  className="inline-flex items-center gap-2 px-6 h-14 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-sm hover:bg-[#14230A] hover:text-[#FDF0DF]"
                >
                  Le festival <ArrowDown className="w-4 h-4" />
                </a>
              </motion.div>
            </div>

            {/* Right info block */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="md:col-span-4"
            >
              <div className="border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-6 md:p-7">
                <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                  Drop limité
                </div>
                <div className="font-display text-5xl md:text-6xl mt-1 text-[#FF4D00]">
                  {totalStock || "…"}
                </div>
                <div className="font-cond uppercase tracking-[0.2em] text-xs opacity-80">
                  consommations disponibles
                </div>

                <div className="mt-6 h-px bg-[#FDF0DF]/20" />

                <div className="mt-5 space-y-2.5 text-sm">
                  <div className="flex gap-3 items-center">
                    <Calendar className="w-4 h-4 flex-shrink-0" />
                    Samedi 20 juin 2026
                  </div>
                  <div className="flex gap-3 items-center">
                    <Clock className="w-4 h-4 flex-shrink-0" />
                    20h00 — 00h00
                  </div>
                  <div className="flex gap-3 items-center">
                    <MapPin className="w-4 h-4 flex-shrink-0" />
                    Grand’Place, Roubaix
                  </div>
                </div>

                <div className="mt-6 h-px bg-[#FDF0DF]/20" />

                <div className="mt-5">
                  <Countdown targetIso={sales?.festival_iso || "2026-06-20T20:00:00+02:00"} />
                </div>

                {sales?.discount_pct > 0 && (
                  <div
                    data-testid="hero-discount-banner"
                    className="mt-5 bg-[#FF4D00] text-[#FDF0DF] p-4 -mx-1"
                  >
                    <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-90">
                      ★ Early-bird actif
                    </div>
                    <div className="font-display text-3xl leading-none mt-1">
                      −{sales.discount_pct}% sur ta commande
                    </div>
                    <div className="text-xs mt-2 opacity-90">
                      Remise appliquée automatiquement au panier (hors caution ecocup).
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-t-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] py-3 overflow-hidden">
          <div className="marquee">
            <div className="marquee-track font-display text-2xl md:text-4xl tracking-[0.05em] px-6">
              {Array.from({ length: 12 }).map((_, i) => (
                <span key={i} className="inline-flex items-center gap-6">
                  GRADUR · JUNGELI · EESAH YASUKE
                  <span className="text-[#FF4D00]">★</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PACKS SOIRÉE */}
      <section id="packs" className="relative border-b-2 border-[#14230A]">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-12">
          <div className="grid md:grid-cols-12 gap-6 items-end">
            <div className="md:col-span-7">
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
                Packs Soirée · Économise jusqu’à 8 €
              </div>
              <h2 className="font-display text-5xl md:text-7xl mt-2 leading-[0.9]">
                Compose ton <span className="text-[#FF4D00]">menu</span>.
              </h2>
            </div>
            <div className="md:col-span-5 text-[#14230A]/80 text-base leading-relaxed">
              Food + boissons, configurées à ta sauce. Tu choisis chaque slot dans la liste — wrap ou
              sandwich, mojito, punch ou soft. Prix fixe, économies grasses.
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="border-2 border-[#14230A]/40 h-[360px] animate-pulse bg-[#F0DEC4]" />
                ))
              : packs.map((p) => (
                  <PackCard
                    key={p.id}
                    pack={p}
                    foodProducts={foodProducts}
                    drinkProducts={drinksForPack}
                  />
                ))}
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section id="catalog" className="relative">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-10">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
                La carte
              </div>
              <h2 className="font-display text-5xl md:text-7xl mt-1 leading-none">
                Choisis ton <span className="text-[#FF4D00]">poison</span>
              </h2>
              <p className="mt-3 max-w-xl text-[#14230A]/70">
                Cocktails maison, bières d’abbaye, softs glacés, eau pour les sages. Tout est limité —
                premier arrivé, premier servi.
              </p>
            </div>

            <button
              onClick={() => setOpen(true)}
              className="hidden md:inline-flex items-center gap-2 px-5 h-11 border-2 border-[#14230A] font-cond uppercase tracking-[0.2em] text-xs hover:bg-[#14230A] hover:text-[#FDF0DF]"
            >
              Voir mon panier
            </button>
          </div>

          {/* Filters */}
          <div className="mt-8 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-4 h-10 border-2 font-cond uppercase tracking-[0.2em] text-xs transition-all ${
                  filter === f.id
                    ? "bg-[#14230A] text-[#FDF0DF] border-[#14230A]"
                    : "border-[#14230A] hover:bg-[#FF4D00] hover:border-[#FF4D00] hover:text-[#FDF0DF]"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {loading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <div
                    key={i}
                    className="border-2 border-[#14230A]/40 h-[380px] animate-pulse bg-[#F0DEC4]"
                  />
                ))
              : filtered.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      {/* ECOCUP + CONCOURS */}
      <section id="ecocup-concours" className="border-t-2 border-[#14230A] bg-[#FDF0DF] relative overflow-hidden">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-16 md:py-24">
          {/* Header */}
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-7">
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00] flex items-center gap-2">
                <Recycle className="w-3.5 h-3.5" /> Eco-friendly · Game on
              </div>
              <h2 className="font-display text-5xl md:text-7xl mt-2 leading-[0.9]">
                L’ecocup, <br />
                <span className="text-[#FF4D00]">caution &amp; jeu</span>.
              </h2>
            </div>
            <div className="md:col-span-5 text-[#14230A]/80 text-base md:text-lg leading-relaxed">
              Chaque commande embarque <b>1 ecocup collector Trésors de Guyane × URBX</b> à 2 €. Tu peux
              en prendre plusieurs si tu viens en équipe — la caution est remboursée en espèces au stand
              dès que tu rends la cup.
            </div>
          </div>

          {/* Ecocup card + add more */}
          {ecocup && (
            <div className="mt-10 grid md:grid-cols-12 gap-5 items-stretch">
              <div className="md:col-span-7 border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-6 md:p-8 relative overflow-hidden">
                <div className="absolute -right-10 -top-10 opacity-[0.08] pointer-events-none">
                  <TopoEmblem className="w-72 h-72" color="#FDF0DF" />
                </div>
                <div className="relative">
                  <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                    Inclus automatiquement
                  </div>
                  <div className="font-display text-4xl md:text-5xl mt-1">EcoCup Collector</div>
                  <div className="mt-3 text-sm opacity-85 max-w-md">
                    Cup réutilisable aux couleurs Trésors de Guyane × URBX. Caution {ecocup.price_eur.toFixed(0)}&nbsp;€,
                    remboursée intégralement en espèces au retour au stand.
                  </div>
                  <div className="mt-5 flex items-center gap-3">
                    <span className="font-display text-5xl text-[#FF4D00]">
                      {ecocup.price_eur.toFixed(0)}
                    </span>
                    <span className="font-cond uppercase tracking-widest text-sm opacity-70">
                      € · caution
                    </span>
                  </div>
                  <button
                    onClick={() => {
                      add(ecocup, 1);
                      toast.success("1 ecocup ajoutée");
                    }}
                    data-testid="ecocup-add-extra"
                    className="mt-6 inline-flex items-center gap-2 px-5 h-11 bg-[#FDF0DF] text-[#14230A] font-cond uppercase tracking-[0.2em] text-xs font-bold hover:bg-[#FF4D00] hover:text-[#FDF0DF]"
                  >
                    <Plus className="w-4 h-4" /> Ajouter une cup en plus
                  </button>
                </div>
              </div>

              {/* Prize 1 */}
              <div className="md:col-span-5 border-2 border-[#FF4D00] bg-[#FF4D00] text-[#FDF0DF] p-6 md:p-7 relative flex flex-col">
                <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-90 flex items-center gap-2">
                  <Trophy className="w-3.5 h-3.5" /> Lot N°1 · Golden Ticket
                </div>
                <div className="font-display text-3xl md:text-4xl mt-2 leading-tight">
                  Un <span className="text-[#14230A]">iPhone 14</span>
                  <br />
                  reconditionné
                </div>
                <p className="mt-4 text-sm leading-relaxed opacity-95">
                  Un Golden Ticket est planqué <b>sous l’une des ecocups</b>. Sur ta cup, tu trouves un
                  numéro unique. Entre-le sur la <b>roue de la chance</b> du site pour découvrir
                  immédiatement si tu remportes l’iPhone.
                </p>
                <Link
                  to="/jeu"
                  data-testid="concours-go-wheel"
                  className="mt-5 inline-flex items-center gap-2 px-4 h-11 bg-[#14230A] text-[#FF4D00] font-cond uppercase tracking-[0.2em] text-xs font-bold hover:bg-[#FDF0DF] hover:text-[#14230A] transition-colors"
                >
                  🎡 Tester mon numéro de cup
                </Link>
                <div className="mt-auto pt-5 font-cond uppercase tracking-[0.25em] text-[10px] opacity-80">
                  Tirage instantané · 1 numéro = 1 essai
                </div>
              </div>
            </div>
          )}

          {/* Prize 2 (instagram) full width */}
          <div className="mt-5 border-2 border-[#14230A] p-6 md:p-8 relative overflow-hidden">
            <div className="grid md:grid-cols-12 gap-6 items-center">
              <div className="md:col-span-7">
                <div className="font-cond uppercase tracking-[0.3em] text-[10px] text-[#FF4D00] flex items-center gap-2">
                  <Instagram className="w-3.5 h-3.5" /> Lot N°2 · Dernière chance · Instagram
                </div>
                <div className="font-display text-3xl md:text-5xl mt-2 leading-tight">
                  150&nbsp;€ à dépenser sur <span className="text-[#FF4D00]">tresorsdeguyane.fr</span>
                </div>
                <ol className="mt-5 grid sm:grid-cols-2 gap-3 text-sm">
                  {[
                    "Pas gagné à la roue ? T’as une seconde chance.",
                    "Va sur le post URBX 2026 @tresorsdeguyane.",
                    "Like + commente 2 amis + abonne-toi.",
                    "Gagnant tiré au sort & annoncé à la fin du festival.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="font-display text-2xl text-[#FF4D00] leading-none w-6">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="text-[#14230A]/85 pt-0.5">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="md:col-span-5">
                <div className="relative aspect-square border-2 border-[#14230A] bg-[#FDF0DF] flex items-center justify-center p-8">
                  <div className="absolute inset-2 border border-[#14230A]/15" />
                  <div className="text-center relative">
                    <Gift className="w-12 h-12 mx-auto text-[#FF4D00]" />
                    <div className="font-display text-3xl mt-4 leading-tight">
                      150&nbsp;€
                    </div>
                    <div className="font-cond uppercase tracking-[0.3em] text-[10px] mt-1 text-[#14230A]/70">
                      Bon d’achat
                    </div>
                    <div className="mt-3 font-cond uppercase tracking-[0.2em] text-xs text-[#14230A]">
                      tresorsdeguyane.fr
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* EVENT INFO */}
      <section id="event" className="bg-[#14230A] text-[#FDF0DF] relative overflow-hidden mt-16">
        <div className="grain absolute inset-0 pointer-events-none" />
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-28 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
                URBX 2026 · Grand’Place
              </div>
              <h2 className="font-display text-5xl md:text-7xl lg:text-8xl mt-2 leading-[0.9]">
                Un concert <br />
                <span className="text-[#FF4D00]">à ciel ouvert</span>
              </h2>
              <p className="mt-6 max-w-xl text-[#FDF0DF]/80 leading-relaxed">
                Le 20 juin, la Grand’Place de Roubaix se transforme en scène ouverte pour l’un des
                grands temps forts d’URBX 2026. <b>Gradur, Jungeli, Eesah Yasuke</b> — rap, afro,
                mélodies populaires, énergie live. Trésors de Guyane tient le bar.
              </p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="border-2 border-[#FDF0DF] p-4">
                  <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                    Date
                  </div>
                  <div className="font-display text-2xl mt-1">20 JUIN</div>
                  <div className="text-sm opacity-80">2026 · Samedi</div>
                </div>
                <div className="border-2 border-[#FDF0DF] p-4">
                  <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                    Heure
                  </div>
                  <div className="font-display text-2xl mt-1">20H — 00H</div>
                  <div className="text-sm opacity-80">Portes 19h</div>
                </div>
                <div className="border-2 border-[#FDF0DF] p-4">
                  <div className="font-cond uppercase tracking-[0.3em] text-[10px] opacity-70">
                    Entrée
                  </div>
                  <div className="font-display text-2xl mt-1">GRATUITE</div>
                  <div className="text-sm opacity-80">Mineurs accompagnés</div>
                </div>
              </div>
            </div>

            <div className="md:col-span-5">
              <div className="border-2 border-[#FDF0DF] p-6 md:p-8 bg-[#213B12]">
                <div className="font-cond uppercase tracking-[0.3em] text-[11px] opacity-70">
                  Comment ça marche
                </div>
                <ol className="mt-5 space-y-5">
                  {[
                    "Choisis tes boissons (ecocup auto-ajoutée).",
                    "Paye en ligne (sécurisé Stripe).",
                    "Reçois ton ticket QR sur l’email.",
                    "Le 20/06, scan au bar — caution cup remboursée en espèces au retour.",
                  ].map((step, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-display text-3xl text-[#FF4D00] leading-none w-8">
                        0{i + 1}
                      </span>
                      <span className="text-sm leading-relaxed pt-1">{step}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="max-w-[1440px] mx-auto px-5 md:px-10 py-20 md:py-24">
        <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">FAQ</div>
        <h2 className="font-display text-5xl md:text-7xl mt-1 leading-none">Vite répondu.</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-x-12 gap-y-6">
          {[
            {
              q: "C'est quoi l'ecocup à 2 € ?",
              a: "Une cup réutilisable Trésors de Guyane × URBX, ajoutée auto à ta commande. Ce sont 2 € de caution remboursés cash au stand quand tu rends la cup. Et sous une cup mystère se planque le Golden Ticket pour gagner un iPhone 14.",
            },
            {
              q: "Est-ce que je peux récupérer le jour J ?",
              a: "Oui, présente ton QR au stand Trésors de Guyane le 20 juin entre 20h et minuit. Une consommation = un coup de scan.",
            },
            {
              q: "Comment marche le jeu Instagram ?",
              a: "Chaque ecocup a un sticker numéroté. Scanne son QR, like le post URBX × @tresorsdeguyane, commente 2 amis, abonne-toi. Un gagnant sera tiré au sort à la fin du festival pour remporter 150 € à dépenser sur tresorsdeguyane.fr.",
            },
            {
              q: "Que se passe-t-il si un produit est en rupture ?",
              a: "Si le stock tombe à zéro avant que tu termines, il est marqué Sold out. Pas de panique : on t’avertit dès qu’un produit s’approche de la rupture.",
            },
            {
              q: "Mineurs ?",
              a: "Pas d’alcool pour les moins de 18 ans. Plein de softs, jus, eaux, mojito zéro, punch zéro — la teuf marche aussi sans alcool.",
            },
            {
              q: "Et si je peux pas venir ?",
              a: "Préviens-nous via Instagram @tresorsdeguyane, on regarde au cas par cas selon le délai.",
            },
          ].map((it) => (
            <div key={it.q} className="border-t-2 border-[#14230A] pt-5">
              <h3 className="font-display text-2xl md:text-3xl leading-tight">{it.q}</h3>
              <p className="mt-2 text-sm md:text-base text-[#14230A]/75 leading-relaxed">{it.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RESEND TICKET */}
      <ResendTicketSection />

      <Footer />
      <CartDrawer />
    </div>
  );
}


function ResendTicketSection() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      toast.error("Email invalide");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/ticket/resend`, { email: trimmed });
      toast.success(data.message || "Si une commande existe pour cet email, le ticket vient d'être renvoyé.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur, réessaie plus tard");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="resend-ticket"
      data-testid="resend-ticket-section"
      className="max-w-[1440px] mx-auto px-5 md:px-10 py-12 md:py-16"
    >
      <div className="border-2 border-[#14230A] bg-[#14230A] text-[#FDF0DF] p-6 md:p-10 grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="font-cond uppercase tracking-[0.3em] text-[11px] text-[#FF4D00]">
            <Mail className="w-3.5 h-3.5 inline mr-2" /> Email perdu ?
          </div>
          <h3 className="font-display text-3xl md:text-5xl mt-2 leading-none">
            Renvoyer mon ticket
          </h3>
          <p className="mt-3 text-sm opacity-80 max-w-md leading-relaxed">
            Tape l&apos;email utilisé lors de l&apos;achat — on te renvoie le mail avec ton QR code de récupération.
          </p>
        </div>
        <form onSubmit={onSubmit} className="flex gap-2 flex-col sm:flex-row">
          <input
            data-testid="resend-ticket-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ton@email.com"
            required
            className="flex-1 h-12 px-3 bg-[#FDF0DF]/10 border-2 border-[#FDF0DF]/40 text-[#FDF0DF] placeholder:text-[#FDF0DF]/40 font-cond focus:outline-none focus:border-[#FF4D00]"
          />
          <button
            data-testid="resend-ticket-submit"
            type="submit"
            disabled={loading}
            className="h-12 px-5 bg-[#FF4D00] text-[#FDF0DF] font-cond uppercase tracking-[0.2em] text-xs font-bold disabled:opacity-50 inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Renvoyer
          </button>
        </form>
      </div>
    </section>
  );
}
